// src/components/layout/settings/CustomEndpointsSection.jsx
// Lets PRO users add/remove their own custom remote endpoints.
// Config (displayName, baseUrl, modelId) → Firestore users/{uid}/customEndpoints/{id}
// API key → keyStateManager.updateKeys({ 'CUSTOM_<UPPERCASE_UUID>': key })

import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, deleteDoc, doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { app } from '@/firebase/config';
import { authManager, TIER_HIERARCHY, TIERS } from '@/managers/AuthManager';
import { keyStateManager } from '@/managers/zKeysManager';
import { testCustomEndpoint } from './TestTheAPI';
import { Plus, Trash2, Server, Check } from 'lucide-react';

const EMPTY_FORM = { displayName: '', baseUrl: '', modelId: '', apiKey: '' };

function isProTier(tier) {
    const rank = TIER_HIERARCHY[tier] ?? -1;
    return rank >= (TIER_HIERARCHY[TIERS.PRO] ?? 2);
}

export function CustomEndpointsSection() {
    const [authState, setAuthState] = useState(() => authManager.getState());
    const [endpoints, setEndpoints] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [saveStatus, setSaveStatus] = useState('idle'); // idle | testing | saving
    const [error, setError] = useState('');
    const [newlyAddedId, setNewlyAddedId] = useState(null);
    const firestore = getFirestore(app);

    useEffect(() => {
        const unsub = authManager.subscribe(setAuthState);
        return unsub;
    }, []);

    const userId = authState.user?.uid;
    const isPro = isProTier(authState.tier);

    useEffect(() => {
        if (!userId) {
            setEndpoints([]);
            return;
        }
        const colRef = collection(firestore, `users/${userId}/customEndpoints`);
        const unsub = onSnapshot(colRef, (snap) => {
            const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            docs.sort((a, b) => (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0));
            setEndpoints(docs);
        }, () => setEndpoints([]));
        return unsub;
    }, [userId]);

    const handleTestAndSave = async () => {
        const { displayName, baseUrl, modelId, apiKey } = form;
        if (!displayName.trim() || !baseUrl.trim() || !modelId.trim() || !apiKey.trim()) {
            setError('All fields are required.');
            return;
        }
        // Normalize: auto-prepend https:// if the user omitted the scheme
        let normalizedUrl = baseUrl.trim();
        if (!/^https?:\/\//i.test(normalizedUrl)) {
            normalizedUrl = 'https://' + normalizedUrl;
        }
        normalizedUrl = normalizedUrl.replace(/\/+$/, '');

        setError('');
        setSaveStatus('testing');
        try {
            const testResult = await testCustomEndpoint(normalizedUrl, modelId.trim(), apiKey.trim());
            if (!testResult.valid) {
                setError(`Test failed: ${testResult.message}`);
                setSaveStatus('idle');
                return;
            }
        } catch (err) {
            setError(`Test error: ${err.message}`);
            setSaveStatus('idle');
            return;
        }

        setSaveStatus('saving');
        try {
            const docRef = await addDoc(
                collection(firestore, `users/${userId}/customEndpoints`),
                {
                    displayName: displayName.trim(),
                    baseUrl: normalizedUrl,
                    modelId: modelId.trim(),
                    createdAt: serverTimestamp(),
                }
            );
            await keyStateManager.updateKeys({ [`CUSTOM_${docRef.id.toUpperCase()}`]: apiKey.trim() });
            setNewlyAddedId(docRef.id);
            setForm(EMPTY_FORM);
            setShowForm(false);
            setTimeout(() => setNewlyAddedId(null), 3000);
        } catch (err) {
            setError(`Failed to save: ${err.message}`);
        } finally {
            setSaveStatus('idle');
        }
    };

    const handleDelete = async (endpoint) => {
        if (!window.confirm(`Remove "${endpoint.displayName}"? This also deletes its API key.`)) return;
        try {
            await deleteDoc(doc(firestore, `users/${userId}/customEndpoints/${endpoint.id}`));
            await keyStateManager.deleteKey(`CUSTOM_${endpoint.id.toUpperCase()}`);
        } catch {
            // Silent — Firestore snapshot will reflect the current state
        }
    };

    if (!userId) return null;

    if (!isPro) {
        return (
            <div className="p-4 sm:p-5 rounded-xl border border-border-subtle bg-surface-raised">
                <div className="flex items-center gap-2 mb-2">
                    <Server className="w-5 h-5 text-on-surface-dim" />
                    <h3 className="text-lg font-medium text-on-surface">Custom Endpoints</h3>
                    <button
                        type="button"
                        onClick={() => window.dispatchEvent(new CustomEvent('shelbula:open-settings-subscription'))}
                        className="text-xs font-semibold text-warning bg-warning/10 hover:bg-warning/20 px-1.5 py-0.5 rounded transition-colors"
                    >
                        PRO ONLY
                    </button>
                </div>
                <p className="text-sm text-on-surface-dim">
                    Custom Endpoints let you connect any self-hosted or cloud LLM server (RunPod, vLLM, Ollama with a public tunnel, etc.).
                </p>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-5 rounded-xl border border-border-subtle bg-surface-raised">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Server className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-medium text-on-surface">Custom Endpoints</h3>
                </div>
                {!showForm && (
                    <button
                        onClick={() => setShowForm(true)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Add Endpoint
                    </button>
                )}
            </div>

            <p className="text-xs text-on-surface-dim mb-4">
                Connect any self-hosted or cloud LLM server. Each endpoint appears as a model in your picker.
            </p>

            {/* Existing endpoints */}
            {endpoints.length > 0 && (
                <div className="space-y-2 mb-4">
                    {endpoints.map((ep) => (
                        <div
                            key={ep.id}
                            className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${
                                ep.id === newlyAddedId
                                    ? 'border-emerald-500/50 bg-emerald-500/10'
                                    : 'border-border-subtle bg-surface'
                            }`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                    {ep.id === newlyAddedId && <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                                    <div className="text-sm font-medium text-on-surface truncate">{ep.displayName}</div>
                                    {ep.id === newlyAddedId && <span className="text-[10px] text-emerald-500 font-medium shrink-0">live in picker</span>}
                                </div>
                                <div className="text-xs text-on-surface-dim truncate">{ep.baseUrl} · <span className="font-mono">{ep.modelId}</span></div>
                                <div className="mt-1 text-[10px] text-on-surface-dim">Tool-capable turns auto-use the structured JSON envelope.</div>
                            </div>
                            <button
                                onClick={() => handleDelete(ep)}
                                className="ml-3 flex-shrink-0 text-on-surface-dim hover:text-error transition-colors"
                                title="Remove endpoint"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Add form */}
            {showForm && (
                <div className="space-y-3 pt-3 border-t border-border-subtle">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-on-surface-dim mb-1">Display Name</label>
                            <input
                                type="text"
                                placeholder="My Private LLM"
                                value={form.displayName}
                                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                                className="w-full px-3 py-2 text-sm bg-surface border border-border-subtle rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 text-on-surface placeholder:text-on-surface-dim"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-on-surface-dim mb-1">Model ID</label>
                            <input
                                type="text"
                                placeholder="meta-llama/Llama-3-8b-chat-hf"
                                value={form.modelId}
                                onChange={(e) => setForm((f) => ({ ...f, modelId: e.target.value }))}
                                className="w-full px-3 py-2 text-sm bg-surface border border-border-subtle rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 text-on-surface placeholder:text-on-surface-dim font-mono"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs text-on-surface-dim mb-1">Base URL</label>
                        <input
                            type="url"
                            placeholder="https://your-endpoint.runpod.io"
                            value={form.baseUrl}
                        onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
                                className="w-full px-3 py-2 text-sm bg-surface border border-border-subtle rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 text-on-surface placeholder:text-on-surface-dim"
                            />
                        <p className="text-[10px] text-on-surface-dim mt-1">
                            Should expose <span className="font-mono">/v1/chat/completions</span> and optionally <span className="font-mono">/v1/models</span>. Ollama users: expose via ngrok or Cloudflare Tunnel.
                        </p>
                    </div>
                    <div>
                        <label className="block text-xs text-on-surface-dim mb-1">API Key</label>
                        <input
                            type="password"
                            placeholder="Bearer token / API key"
                            value={form.apiKey}
                            onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
                            autoComplete="off"
                            className="w-full px-3 py-2 text-sm bg-surface border border-border-subtle rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 text-on-surface placeholder:text-on-surface-dim"
                        />
                    </div>
                    {error && <p className="text-xs text-error">{error}</p>}
                    <div className="flex items-center gap-2 pt-1">
                        <button
                            onClick={handleTestAndSave}
                            disabled={saveStatus !== 'idle'}
                            className="px-4 py-1.5 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary-dark disabled:opacity-50 transition-colors"
                        >
                            {saveStatus === 'testing' ? 'Testing…' : saveStatus === 'saving' ? 'Saving…' : 'Test & Save'}
                        </button>
                        <button
                            onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setError(''); }}
                            className="px-4 py-1.5 text-sm text-on-surface-dim hover:text-on-surface transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {endpoints.length === 0 && !showForm && (
                <p className="text-xs text-on-surface-dim italic">No custom endpoints yet. Click "Add Endpoint" to connect your first model.</p>
            )}
        </div>
    );
}
